import { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { Loader2, GitBranch, FileText, Calendar, Tag, Plus } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";

import { CreateVersionDialog } from "./CreateVersionDialog";

interface TemplateDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: string;
  onVersionSelect?: (versionId: string) => void;
}

export const TemplateDetailDialog = ({
  open,
  onOpenChange,
  templateId,
  onVersionSelect,
}: TemplateDetailDialogProps) => {
  const [showCreateVersionDialog, setShowCreateVersionDialog] = useState(false);

  const { data: template, isLoading: templateLoading } = useQuery({
    queryKey: ["template-detail", templateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("prompt_templates" as any)
        .select("*")
        .eq("id", templateId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching template:", error);
        throw error;
      }
      return data;
    },
    enabled: open && !!templateId,
  });

  const { data: versions, isLoading: versionsLoading, refetch: refetchVersions } = useQuery({
    queryKey: ["template-versions", templateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("prompt_versions" as any)
        .select("*")
        .eq("template_id", templateId)
        .order("version", { ascending: false });

      if (error) {
        console.error("Error fetching versions:", error);
        throw error;
      }
      return data;
    },
    enabled: open && !!templateId,
  });

  const handleCreateVersionSuccess = () => {
    setShowCreateVersionDialog(false);
    refetchVersions();
    toast.success("Version created successfully");
  };

  const isLoading = templateLoading || versionsLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Template Details
          </DialogTitle>
          <DialogDescription>
            View template information and version history
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {!isLoading && template && (
          <ScrollArea className="max-h-[70vh]">
            <div className="space-y-6 pr-4">
              {/* Template Info */}
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-xl">{(template as any).name}</CardTitle>
                    <Badge variant={(template as any).status === "active" ? "default" : "secondary"}>
                      {(template as any).status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Description</h4>
                    <p className="text-sm">{(template as any).description || "No description"}</p>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2">
                        <Tag className="h-4 w-4" />
                        Category
                      </h4>
                      <p className="text-sm capitalize">{(template as any).category}</p>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Created
                      </h4>
                      <p className="text-sm">
                        {new Date((template as any).created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Template Key</h4>
                    <code className="text-xs bg-muted px-2 py-1 rounded">{(template as any).template_key}</code>
                  </div>
                </CardContent>
              </Card>

              {/* Version History */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <GitBranch className="h-5 w-5" />
                      Version History ({versions?.length || 0})
                    </CardTitle>
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowCreateVersionDialog(true);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      New Version
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {versions && versions.length > 0 ? (
                    <div className="space-y-3">
                      {versions.map((version: any) => (
                        <Card
                          key={version.id}
                          className="cursor-pointer hover:bg-accent/50 transition-colors"
                          onClick={() => onVersionSelect?.(version.id)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge variant="outline">v{version.version}</Badge>
                                  {version.is_latest_version && (
                                    <Badge>Latest</Badge>
                                  )}
                                  {version.status === 'active' && (
                                    <Badge variant="secondary">Active</Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {version.change_notes || "No change notes"}
                                </p>
                              </div>
                              <div className="text-right text-xs text-muted-foreground">
                                {new Date(version.created_at).toLocaleDateString()}
                              </div>
                            </div>
                            <div className="mt-2">
                              <ScrollArea className="h-[60px] w-full rounded-md border p-2">
                                <pre className="text-xs font-mono whitespace-pre-wrap">
                                  {typeof version.content === 'object' 
                                    ? JSON.stringify(version.content, null, 2).substring(0, 200)
                                    : version.content?.substring(0, 200) || 'No content'
                                  }...
                                </pre>
                              </ScrollArea>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <GitBranch className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">No versions yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </ScrollArea>
        )}

        {!isLoading && !template && (
          <div className="text-center py-8">
            <p className="text-destructive">Template not found</p>
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </div>

        {/* Create Version Dialog */}
        <CreateVersionDialog
          open={showCreateVersionDialog}
          onOpenChange={setShowCreateVersionDialog}
          onSuccess={handleCreateVersionSuccess}
          initialTemplateId={templateId}
        />
      </DialogContent>
    </Dialog>
  );
};
